import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { useField } from 'formik';
import { isNull } from 'lodash';

const FormikDatePicker = props => {
  const {
    name,
    datatype,
    value,
    disabled,
    onBlur,
    onChange,
    initialValue, // supplies payload value ahead of formik loading.
  } = props;

  const [field, , helpers] = useField(name);
  const [dateValue, setDateValue] = useState(null);

  const castByTimezone = date => new Date((typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', { timeZone: 'America/Edmonton' }));

  const getFormattedDate = inputDate => {
    const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(inputDate);
    const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(inputDate);
    const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(inputDate);
    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  };

  const getFormattedTime = inputDate => new Intl.DateTimeFormat('en', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).format(inputDate);

  const getFormattedDateTime = inputDate => `${getFormattedDate(inputDate)} ${getFormattedTime(inputDate)}`;

  const getFormattedDateValue = inputDate => {
    switch (datatype) {
      case 'datetime':
        return getFormattedDateTime(inputDate);
      case 'date':
        return getFormattedDate(inputDate);
      case 'time':
        return getFormattedTime(inputDate);
      default:
        return inputDate.toLocaleString();
    }
  };

  const getIsoFormat = seed => {
    if (seed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/g)) {
      const iso = new Date(seed).toISOString();
      const tzCast = castByTimezone(iso);
      return tzCast;
    }

    const localeOptions = {
      timeZoneName: 'longOffset',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    };
    const localeFormatter = new Intl.DateTimeFormat([], localeOptions);

    if (datatype === 'datetime') {
      const localeDT = localeFormatter.format(new Date(seed));
      return new Date(localeDT);
    }
    if (datatype === 'date') {
      const localeDate = seed.replaceAll('-', '/');
      const dateObj = new Date(localeDate);
      return dateObj;
    }
    if (datatype === 'time') {
      const todayYYMMDD = getFormattedDate(new Date());
      const timeStamp = `${todayYYMMDD} ${seed}`;
      const timeString = localeFormatter.format(new Date(timeStamp));
      return new Date(timeString);
    }

    return new Date().toISOString();
  };

  // populates field with Flexrule payload
  useMemo(() => {
    if (!initialValue && !value) return;
    const seedValue = !value ? initialValue : value;
    const receivedDate = getIsoFormat(seedValue);

    if (datatype === 'time') {
      const year = receivedDate.getFullYear();
      if (year < 1907) {
        const newValue = value.replace(`${year}`, '1907');
        const castValue = castByTimezone(newValue);
        setDateValue(new Date(castValue));
      }
    }
    setDateValue(receivedDate);
  }, []);

  const handleChange = (val = new Date()) => {
    setDateValue(val);
    // required FlexRuleFormat if not null date value
    const dateVal = isNull(val) ? '' : getFormattedDateValue(val);
    if (field?.value === dateVal) return;
    if (onChange) {
      onChange(dateVal);
    } else {
      helpers.setValue(dateVal);
    }
  };

  // Forces user to select date from datepicker only and prevents typing any values which caused a bug
  const handleTypedInputs = e => {
    e.preventDefault();
  };

  const DateWithAddOnCalendar = React.forwardRef(({
    className: _className, val, onClick, ...remaining
  }, ref) => (
    <div className="input-group">
      <input className="form-control text-input" type="text" ref={ref} defaultValue={val} {...remaining} />
      <div className="input-group-append">
        <button className="btn btn-primary btn-sm" type="button" onClick={onClick}>
          <i className="far fa-calendar-alt" />
        </button>
      </div>
    </div>
  ));

  DateWithAddOnCalendar.propTypes = {
    className: PropTypes.string, val: PropTypes.string, onClick: PropTypes.func,
  };

  const TimeWithAddOnClock = React.forwardRef(({
    className: _className, val, onClick, ...remaining
  }, ref) => (
    <div className="input-group">
      <input className="form-control text-input" type="text" ref={ref} defaultValue={val} {...remaining} />
      <div className="input-group-append">
        <button className="btn btn-primary btn-sm" type="button" onClick={onClick}>
          <i className="far fa-alarm-clock" />
        </button>
      </div>
    </div>
  ));

  TimeWithAddOnClock.propTypes = {
    className: PropTypes.string, val: PropTypes.string, onClick: PropTypes.func,
  };

  const [customInput, dateFormat, todayButton, showTimeSelect, showTimeSelectOnly] = useMemo(() => {
    let input = null;
    let format = 'yyyy/MM/dd HH:mm';
    let today = null;
    let timeSelect = true;
    let timeSelectOnly = false;

    switch (datatype) {
      case 'date':
        input = <DateWithAddOnCalendar />;
        format = 'yyyy-MM-dd';
        today = 'Today';
        timeSelect = false;
        timeSelectOnly = false;
        break;
      case 'time':
        input = <TimeWithAddOnClock />;
        format = 'h:mm aa';
        today = '';
        timeSelect = true;
        timeSelectOnly = true;
        break;
      case 'datetime':
        input = <DateWithAddOnCalendar />;
        format = 'yyyy-MM-dd HH:mm';
        today = 'Today';
        timeSelect = true;
        timeSelectOnly = false;
        break;
      default:
        input = <DateWithAddOnCalendar />;
        format = 'yyyy-MM-dd';
        today = 'Today';
        timeSelect = true;
        timeSelectOnly = false;
        break;
    }

    return [input, format, today, timeSelect, timeSelectOnly];
  });

  return (
    <DatePicker
      {...props}
      id={name}
      name={name}
      customInput={customInput}
      closeOnScroll
      dateFormat={dateFormat}
      showTimeSelect={showTimeSelect}
      showTimeSelectOnly={showTimeSelectOnly}
      timeCaption="Time"
      todayButton={todayButton}
      popperPlacement="bottom-end"
      autoComplete="off"
      disabled={disabled}
      fixedHeight
      timeIntervals={15}
      isClearable={!disabled}
      onBlur={onBlur}
      onChange={date => handleChange(date)}
      onChangeRaw={handleTypedInputs}
      value={dateValue} // to show date values
      selected={dateValue} // to show time values
    />
  );
};

FormikDatePicker.propTypes = {
  disabled: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  datatype: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  maxLength: PropTypes.number,
  initialValue: PropTypes.string,
};

export default FormikDatePicker;
